import React, { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

const PairTable = () => {
    const { t } = useTranslation();
    const [tickerData, setTickerData] = useState({});
    const [isConnected, setIsConnected] = useState(false);
    const [updatingItems, setUpdatingItems] = useState(new Set());
    const wsRef = useRef(null);

    // Coin metadata for display
    const coinMetadata = {
        'BTCUSDT': {
            image: "https://assets.coingecko.com/coins/images/1/large/bitcoin.png",
            title: "BTC",
            description: "Bitcoin"
        },
        'ETHUSDT': {
            image: "https://assets.coingecko.com/coins/images/279/large/ethereum.png",
            title: "ETH",
            description: "Ethereum"
        },
        'ADAUSDT': {
            image: "https://assets.coingecko.com/coins/images/975/large/cardano.png",
            title: "ADA",
            description: "Cardano"
        },
        'SOLUSDT': {
            image: "https://assets.coingecko.com/coins/images/4128/large/solana.png",
            title: "SOL",
            description: "Solana"
        },
        'DOGEUSDT': {
            image: "https://assets.coingecko.com/coins/images/5/large/dogecoin.png",
            title: "DOGE",
            description: "Dogecoin"
        },
        'AVAXUSDT': {
            image: "https://assets.coingecko.com/coins/images/12559/large/Avalanche_Circle_RedWhite_Trans.png",
            title: "AVAX",
            description: "Avalanche"
        },
        'LTCUSDT': {
            image: "https://assets.coingecko.com/coins/images/2/large/litecoin.png",
            title: "LTC",
            description: "Litecoin"
        },
        'DOTUSDT': {
            image: "https://assets.coingecko.com/coins/images/12171/large/polkadot.png",
            title: "DOT",
            description: "Polkadot"
        },
        'LINKUSDT': {
            image: "https://assets.coingecko.com/coins/images/877/large/chainlink-new-logo.png",
            title: "LINK",
            description: "Chainlink"
        }
    };

    // WebSocket connection for real-time data
    useEffect(() => {
        const connectWebSocket = () => {
            try {
                // Binance WebSocket streams for popular trading pairs
                const streams = [
                    'btcusdt@ticker',
                    'ethusdt@ticker',
                    'adausdt@ticker',
                    'solusdt@ticker',
                    'dogeusdt@ticker',
                    'avaxusdt@ticker',
                    'ltcusdt@ticker',
                    'dotusdt@ticker',
                    'linkusdt@ticker'
                ];

                const wsUrl = `wss://stream.binance.com:9443/stream?streams=${streams.join('/')}`;
                const ws = new WebSocket(wsUrl);
                wsRef.current = ws;

                ws.onopen = () => {
                    console.log('PairTable WebSocket connected');
                    setIsConnected(true);
                };

                ws.onmessage = (event) => {
                    try {
                        const data = JSON.parse(event.data);
                        const ticker = data.data || data;

                        if (ticker && ticker.s) {
                            const symbol = ticker.s;
                            const price = parseFloat(ticker.c || 0);
                            const changePercent = parseFloat(ticker.P || 0);
                            const volume = parseFloat(ticker.v || 0);
                            const highPrice = parseFloat(ticker.h || 0);
                            const lowPrice = parseFloat(ticker.l || 0);
                            const openPrice = parseFloat(ticker.o || 0);

                            // Add updating animation
                            setUpdatingItems(prev => new Set([...prev, symbol]));

                            setTickerData(prev => ({
                                ...prev,
                                [symbol]: {
                                    symbol,
                                    price: price.toFixed(3),
                                    change: `${changePercent >= 0 ? '+' : ''}${changePercent.toFixed(2)}%`,
                                    volume: (volume / 1000000).toFixed(2) + 'M',
                                    high24h: highPrice.toFixed(2),
                                    low24h: lowPrice.toFixed(2),
                                    open24h: openPrice.toFixed(2),
                                    timestamp: Date.now()
                                }
                            }));

                            // Remove updating animation after 1 second
                            setTimeout(() => {
                                setUpdatingItems(prev => {
                                    const newSet = new Set(prev);
                                    newSet.delete(symbol);
                                    return newSet;
                                });
                            }, 1000);
                        }
                    } catch (error) {
                        console.error('WebSocket message error:', error);
                    }
                };

                ws.onclose = () => {
                    console.log('PairTable WebSocket disconnected');
                    setIsConnected(false);
                    // Reconnect after 3 seconds
                    setTimeout(connectWebSocket, 3000);
                };

                ws.onerror = (error) => {
                    console.error('WebSocket error:', error);
                    setIsConnected(false);
                };

            } catch (error) {
                console.error('WebSocket connection error:', error);
            }
        };

        connectWebSocket();

        return () => {
            if (wsRef.current) {
                wsRef.current.close();
            }
        };
    }, []);

    // Create dynamic data items from WebSocket data
    const dataItems = Object.keys(coinMetadata).map(symbol => {
        const metadata = coinMetadata[symbol];
        const realTimeData = tickerData[symbol];
        const isUpdating = updatingItems.has(symbol);

        return {
            ...metadata,
            symbol,
            price: realTimeData?.price || '0.000000',
            change: realTimeData?.change || '+0.00%',
            volume: realTimeData?.volume || '0.00M',
            average: realTimeData?.high24h || '0.000000', // 24 saatlik yüksek fiyat
            isUpdating
        };
    });

    return (
        <div className='pair-table-content'>
            <div className='container'>
                <div className='pair-table-header'>
                    <div className='row'>
                        <div className="col-lg-3">
                            <p className='pair-table-title'>
                                {t('pairTable.coins')}
                                <span className={`ms-2 badge ${isConnected ? 'bg-success' : 'bg-danger'}`}>
                                    {isConnected ? t('pairTable.live') : t('pairTable.noConnection')}
                                </span>
                            </p>
                        </div>
                        <div className="col-lg-2">
                            <p className='pair-table-title'>{t('pairTable.lastPrice')}</p>
                        </div>
                        <div className="col-lg-2">
                            <p className='pair-table-title'>{t('pairTable.change24h')}</p>
                        </div>
                        <div className="col-lg-3">
                            <p className='pair-table-title'>{t('pairTable.high24h')}</p>
                        </div>
                        <div className="col-lg-2">
                            <p className='pair-table-title'>{t('pairTable.marketCap')}</p>
                        </div>
                    </div>
                </div>
                <div className='pair-table-body'>
                    {
                        dataItems.map((item, index) => (
                            <div key={index} className={`row align-items-center pair-table-item py-3 ${item.isUpdating ? 'updating' : ''}`}>
                                <div className="col-lg-3 col-md-3">
                                    <div className='d-flex align-items-start gap-3'>
                                        <img src={item.image} alt="pair-table" width="37px" />
                                        <div className='pair-table-body-item-content'>
                                            <h4>{item.title}</h4>
                                            <p className='mb-0'>{item.description}</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-lg-2 col-md-2">
                                    <p className={`mb-0 price ${item.isUpdating ? 'updating' : ''}`} data-label={t('pairTable.lastPrice')}>{item.price}</p>
                                </div>
                                <div className="col-lg-2 col-md-2">
                                    <p className={`mb-0 change ${item.change.includes('+') ? 'text-success' : 'text-danger'} ${item.isUpdating ? 'updating' : ''}`} data-label={t('pairTable.change24h')}>{item.change}</p>
                                </div>
                                <div className="col-lg-3 col-md-2">
                                    <p className='mb-0' data-label={t('pairTable.high24h')}>{item.average}</p>
                                </div>
                                <div className="col-lg-2 col-md-2">
                                    <p className='mb-0' data-label={t('pairTable.marketCap')}>{item.volume}</p>
                                </div>
                            </div>
                        ))
                    }
                </div>
            </div>
        </div>
    )
}

export default PairTable